import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const LibraryIssue = ({ onNavigate }) => {
    // ── Member verification state ──────────────────────────
    const [memberId, setMemberId] = useState('');
    const [memberInfo, setMemberInfo] = useState(null);
    const [memberError, setMemberError] = useState('');
    const [verifying, setVerifying] = useState(false);

    // ── Book search state ─────────────────────────────────
    const [bookQuery, setBookQuery] = useState('');
    const [bookInfo, setBookInfo] = useState(null);
    const [bookError, setBookError] = useState('');
    const [searchingBook, setSearchingBook] = useState(false);

    // ── Dates ──────────────────────────────────────────────
    const today = new Date().toISOString().split('T')[0];
    const dueDate = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().split('T')[0];
    })();

    // ── Submit state ───────────────────────────────────────
    const [issuing, setIssuing] = useState(false);
    const [issueSuccess, setIssueSuccess] = useState('');
    const [issueError, setIssueError] = useState('');

    // ── Recent issues (live) ───────────────────────────────
    const [recentIssues, setRecentIssues] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

    // ── Fetch recent issues on mount ───────────────────────
    useEffect(() => {
        const fetchRecent = async () => {
            try {
                const res = await fetch(`${API_LINK}/api/library/issues?page=1&limit=5`, {
                    headers: { 'Authorization': `Bearer ${getToken()}` }
                });
                const result = await res.json();
                if (result.success) setRecentIssues(result.data.issues || []);
            } catch (e) {
                console.error('Failed to fetch recent issues', e);
            } finally {
                setLoadingRecent(false);
            }
        };
        fetchRecent();
    }, [issueSuccess]); // re-fetch after a new issue

    // ── Verify member by library card number or student ID ──
    const handleVerifyMember = async () => {
        if (!memberId.trim()) return;
        setVerifying(true);
        setMemberInfo(null);
        setMemberError('');
        try {
            const res = await fetch(`${API_LINK}/api/library/verify-member/${encodeURIComponent(memberId.trim())}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            const result = await res.json();
            if (result.success) {
                setMemberInfo(result.data);
            } else {
                setMemberError(result.message || 'Member not found');
            }
        } catch (e) {
            setMemberError('Network error — could not verify member');
        } finally {
            setVerifying(false);
        }
    };

    // ── Search book by accession no / ISBN / title ──────────
    const handleSearchBook = async () => {
        if (!bookQuery.trim()) return;
        setSearchingBook(true);
        setBookInfo(null);
        setBookError('');
        try {
            const res = await fetch(`${API_LINK}/api/library/books/search?q=${encodeURIComponent(bookQuery.trim())}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            const result = await res.json();
            if (result.success) {
                setBookInfo(result.data.book);
            } else {
                setBookError(result.message || 'Book not found');
            }
        } catch (e) {
            setBookError('Network error — could not search book');
        } finally {
            setSearchingBook(false);
        }
    };

    // ── Confirm issue ───────────────────────────────────────
    const handleConfirmIssue = async () => {
        if (!memberInfo) { setIssueError('Please verify a member first'); return; }
        if (!bookInfo) { setIssueError('Please search and select a book first'); return; }
        if (memberInfo.currently_issued >= memberInfo.max_books_allowed) {
            setIssueError(`${memberInfo.name} has already reached the maximum book limit (${memberInfo.max_books_allowed})`);
            return;
        }

        setIssuing(true);
        setIssueError('');
        setIssueSuccess('');
        try {
            const res = await fetch(`${API_LINK}/api/library/issue`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    book_id: bookInfo.id,
                    member_id: memberInfo.member_id,
                    due_date: dueDate
                })
            });
            const result = await res.json();
            if (result.success) {
                setIssueSuccess(`✅ Book issued successfully! Issue No: ${result.issue_number}`);
                // Reset form
                setMemberId('');
                setMemberInfo(null);
                setBookQuery('');
                setBookInfo(null);
            } else {
                setIssueError(result.message || 'Failed to issue book');
            }
        } catch (e) {
            setIssueError('Network error — could not issue book');
        } finally {
            setIssuing(false);
        }
    };

    // ── Styles ──────────────────────────────────────────────
    const cardStyle = {
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
    };

    const inputStyle = {
        flex: 1,
        padding: '12px 14px',
        border: '1.5px solid #ddd',
        borderRadius: '8px',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'border-color 0.2s'
    };

    const btnGreen = {
        backgroundColor: '#238548',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 20px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '0.9rem',
        whiteSpace: 'nowrap'
    };

    const infoBox = (color, bg, content) => (
        <div style={{ background: bg, border: `1px solid ${color}`, borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.75rem', fontSize: '0.88rem' }}>
            {content}
        </div>
    );

    return (
        <Layout role="librarian" onNavigate={onNavigate} activePage="library-issue" pageTitle="Issue Books">
            <div style={{ padding: '20px' }}>
                <h2 style={{ color: '#333', marginBottom: '2rem' }}>📖 Issue Books</h2>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>

                    {/* ── Issue Form ──────────────────────────── */}
                    <div style={cardStyle}>
                        <h3 style={{ marginTop: 0, color: '#238548', borderBottom: '2px solid #e8f5e9', paddingBottom: '1rem' }}>
                            New Issue Form
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1.5rem' }}>

                            {/* Step 1 — Member */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#444' }}>
                                    Step 1: Student / Member ID
                                </label>
                                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#888' }}>
                                    Enter the student's Library Card Number or Student ID
                                </p>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        placeholder="e.g. SVU2024001 or card number"
                                        value={memberId}
                                        onChange={e => { setMemberId(e.target.value); setMemberInfo(null); setMemberError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && handleVerifyMember()}
                                        style={inputStyle}
                                    />
                                    <button onClick={handleVerifyMember} disabled={verifying || !memberId.trim()} style={{ ...btnGreen, opacity: verifying || !memberId.trim() ? 0.6 : 1 }}>
                                        {verifying ? '...' : '🔍'}
                                    </button>
                                </div>

                                {/* Member found */}
                                {memberInfo && infoBox('#238548', '#e8f5e9',
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong style={{ color: '#238548' }}>✅ Member Verified</strong>
                                            <span style={{ fontSize: '0.75rem', background: memberInfo.status === 'active' ? '#238548' : '#dc3545', color: '#fff', borderRadius: '20px', padding: '2px 10px' }}>
                                                {memberInfo.status}
                                            </span>
                                        </div>
                                        <div style={{ marginTop: '6px', lineHeight: 1.7 }}>
                                            <div><strong>Name:</strong> {memberInfo.name}</div>
                                            <div><strong>ID:</strong> {memberInfo.member_id} {memberInfo.card_number && memberInfo.card_number !== memberInfo.member_id && <span style={{ color: '#888', fontSize: '0.8rem' }}>| Card: {memberInfo.card_number}</span>}</div>
                                            {memberInfo.dept && <div><strong>Dept:</strong> {memberInfo.dept}</div>}
                                            <div>
                                                <strong>Books Issued:</strong> {memberInfo.currently_issued} / {memberInfo.max_books_allowed}
                                                {memberInfo.currently_issued >= memberInfo.max_books_allowed && (
                                                    <span style={{ color: '#dc3545', marginLeft: '8px', fontSize: '0.8rem' }}>⚠️ Limit reached</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Member error */}
                                {memberError && infoBox('#dc3545', '#fff5f5',
                                    <span style={{ color: '#dc3545' }}>❌ {memberError}</span>
                                )}
                            </div>

                            {/* Step 2 — Book */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#444' }}>
                                    Step 2: Book ISBN / Accession No.
                                </label>
                                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#888' }}>
                                    Scan barcode or type accession number, ISBN or title
                                </p>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        placeholder="Accession No. / ISBN / Title"
                                        value={bookQuery}
                                        onChange={e => { setBookQuery(e.target.value); setBookInfo(null); setBookError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && handleSearchBook()}
                                        style={inputStyle}
                                    />
                                    <button onClick={handleSearchBook} disabled={searchingBook || !bookQuery.trim()} style={{ ...btnGreen, opacity: searchingBook || !bookQuery.trim() ? 0.6 : 1 }}>
                                        {searchingBook ? '...' : 'Search'}
                                    </button>
                                </div>

                                {/* Book found */}
                                {bookInfo && infoBox('#1976d2', '#e3f2fd',
                                    <div>
                                        <strong style={{ color: '#1976d2' }}>📚 Book Found</strong>
                                        <div style={{ marginTop: '6px', lineHeight: 1.7 }}>
                                            <div><strong>Title:</strong> {bookInfo.title}</div>
                                            <div><strong>Author:</strong> {bookInfo.author}</div>
                                            <div><strong>Accession:</strong> {bookInfo.accession_number}</div>
                                            {bookInfo.isbn && <div><strong>ISBN:</strong> {bookInfo.isbn}</div>}
                                            <div><strong>Available:</strong> {bookInfo.available_copies} / {bookInfo.total_copies} copies</div>
                                        </div>
                                    </div>
                                )}
                                {/* Book error */}
                                {bookError && infoBox('#dc3545', '#fff5f5',
                                    <span style={{ color: '#dc3545' }}>❌ {bookError}</span>
                                )}
                            </div>

                            {/* Dates */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#555' }}>Issue Date</label>
                                    <input type="date" value={today} readOnly style={{ ...inputStyle, backgroundColor: '#fafafa', cursor: 'not-allowed' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#555' }}>Due Date (14 days)</label>
                                    <input type="date" value={dueDate} readOnly style={{ ...inputStyle, backgroundColor: '#fafafa', cursor: 'not-allowed' }} />
                                </div>
                            </div>

                            {/* Success / Error messages */}
                            {issueSuccess && (
                                <div style={{ background: '#e8f5e9', border: '1px solid #238548', borderRadius: '8px', padding: '1rem', color: '#238548', fontWeight: '600' }}>
                                    {issueSuccess}
                                </div>
                            )}
                            {issueError && (
                                <div style={{ background: '#fff5f5', border: '1px solid #dc3545', borderRadius: '8px', padding: '1rem', color: '#dc3545' }}>
                                    ❌ {issueError}
                                </div>
                            )}

                            {/* Confirm button */}
                            <button
                                onClick={handleConfirmIssue}
                                disabled={issuing || !memberInfo || !bookInfo}
                                style={{
                                    ...btnGreen,
                                    padding: '15px',
                                    fontSize: '1rem',
                                    width: '100%',
                                    opacity: (issuing || !memberInfo || !bookInfo) ? 0.55 : 1,
                                    cursor: (issuing || !memberInfo || !bookInfo) ? 'not-allowed' : 'pointer',
                                    transition: 'opacity 0.2s'
                                }}
                            >
                                {issuing ? '⏳ Issuing...' : '✅ Confirm Issue'}
                            </button>
                        </div>
                    </div>

                    {/* ── Right panel ─────────────────────────── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                        {/* Guidelines */}
                        <div style={{ backgroundColor: '#fff3e0', padding: '1.5rem', borderRadius: '12px', border: '1px solid #ffe0b2' }}>
                            <h3 style={{ marginTop: 0, color: '#f57c00', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                ⚠️ Important Note
                            </h3>
                            <ul style={{ paddingLeft: '1.2rem', color: '#7e4718', lineHeight: '1.7', margin: 0 }}>
                                <li>Maximum <strong>3 books</strong> can be issued per student.</li>
                                <li>Staff members can issue up to <strong>5 books</strong>.</li>
                                <li>Standard issue duration is <strong>14 days</strong>.</li>
                                <li>Check for existing overdue books before issuing new ones.</li>
                                <li>Member ID = student's Library Card Number or Student ID.</li>
                            </ul>
                        </div>

                        {/* Recent Issues (live) */}
                        <div style={{ ...cardStyle, flex: 1 }}>
                            <h3 style={{ marginTop: 0, color: '#333', marginBottom: '1rem' }}>Recent Issues</h3>
                            {loadingRecent ? (
                                <p style={{ color: '#aaa', fontSize: '0.9rem' }}>Loading...</p>
                            ) : recentIssues.length === 0 ? (
                                <p style={{ color: '#aaa', fontSize: '0.9rem' }}>No recent issues found.</p>
                            ) : (
                                <div>
                                    {recentIssues.map(issue => (
                                        <div key={issue.id} style={{
                                            padding: '10px 0',
                                            borderBottom: '1px solid #f0f0f0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: '0.5rem'
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: '500', fontSize: '0.9rem', color: '#333' }}>{issue.book_title}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#888' }}>{issue.member_name} ({issue.member_id})</div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <div style={{ fontSize: '0.75rem', color: '#999' }}>Due: {issue.due_date ? new Date(issue.due_date).toLocaleDateString('en-IN') : '—'}</div>
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    background: issue.status === 'issued' ? '#e8f5e9' : issue.status === 'overdue' ? '#fff5f5' : '#f5f5f5',
                                                    color: issue.status === 'issued' ? '#238548' : issue.status === 'overdue' ? '#dc3545' : '#888',
                                                    fontWeight: '600',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {issue.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default LibraryIssue;
